/**
 * @file App.js
 * @version 1.3.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-09-21
 * @modified 2026-09-19
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Login from './Login';
import Signup from './Signup';
import CreatePassword from './CreatePassword';
import Dashboard from './Dashboard';
import { API_URL } from './config';
import { useNavStore } from './store/navStore';
import { inStackOrder } from './utils/overlayStacking';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentRoute, setCurrentRoute] = useState(window.location.pathname);
  const [loginPhase, setLoginPhase] = useState(null); // null | 'greeting-in' | 'greeting-visible' | 'greeting-out' | 'dashboard-in'
  const [greetingText, setGreetingText] = useState('');
  const [pendingUser, setPendingUser] = useState(null);
  const dashboardReady = useRef(false);
  const greetingMinElapsed = useRef(false);

  useEffect(() => {
    // Handle browser navigation
    const handlePopState = () => {
      setCurrentRoute(window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);

    checkUserAuth();

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Global ESC-to-close for modals. Closes the topmost open .modal-overlay by
  // triggering its close control (.close-btn / .modal-close), falling back to a
  // backdrop click. Registered at app start so it runs before any per-modal ESC
  // handler; when a close button is found we stopImmediatePropagation so a modal
  // that also has its own ESC handler doesn't fire twice (the backdrop fallback
  // does NOT stop, so a modal relying solely on its own ESC still closes).
  // No-ops when no modal is open, leaving other ESC uses (dropdowns, context
  // menus) untouched.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      // Include dialog overlays so that when an alert/confirm sits on top of a
      // modal, ESC targets the dialog (topmost), never the modal beneath it.
      // Sorted by stacking order, NOT taken as the last DOM match: since
      // utils/overlayStacking.js, overlays are stacked by z-index in the order
      // they were opened or last touched, so the last node in the document is
      // not necessarily the one on screen.
      const overlays = inStackOrder(
        document.querySelectorAll('.modal-overlay, .confirm-dialog-overlay')
      ).filter(o => !o.classList.contains('closing') && o.getClientRects().length > 0);
      if (overlays.length === 0) return;
      const top = overlays[overlays.length - 1];
      const closeBtn = top.querySelector('.close-btn, .modal-close, .close-button, .confirm-dialog-close, [data-modal-close]');
      if (closeBtn) {
        e.stopImmediatePropagation();
        closeBtn.click();
      } else {
        top.click();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const checkUserAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/me`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Per-user custom greetings keyed by user ID. To add a new override,
  // add a new entry. Each function receives the current hour (0–23) and
  // the user's first name and must return a string.
  const PERSONAL_GREETINGS = {
    9: (hour, name) => {  // Jade Games
      if (hour < 12) return `Morning ${name}, Amy says Hi Slag`;
      if (hour < 17) return `Afternoon ${name}, what do you meeeeeeeean`;
      return `Evening ${name}, what do you meeeeeeeean`;
    },
    // ID: (hour, name) => `Hi ${name}, message`,   // example
  };

  const getGreeting = (userData) => {
    const hour = new Date().getHours();
    const name = userData.first_name || userData.email.split('@')[0];

    const personal = PERSONAL_GREETINGS[userData.id];
    if (personal) return personal(hour, name);

    if (hour < 12) return `Good Morning, ${name}`;
    if (hour < 17) return `Good Afternoon, ${name}`;
    return `Good Evening, ${name}`;
  };

  // Begin fade-out once both conditions are met: min greeting time AND dashboard ready
  const tryBeginFadeOut = useCallback(() => {
    if (greetingMinElapsed.current && dashboardReady.current) {
      setLoginPhase('greeting-out');
      // Dashboard reveals
      setTimeout(() => {
        setLoginPhase('dashboard-in');
        setPendingUser(prev => {
          if (prev) setUser(prev);
          return null;
        });
        window.history.pushState({}, '', '/');
        setCurrentRoute('/');
      }, 2000);
      // Clean up phase
      setTimeout(() => setLoginPhase(null), 4000);
    }
  }, []);

  const handleLogin = (userData) => {
    const name = userData.first_name || userData.email.split('@')[0];
    dashboardReady.current = false;
    greetingMinElapsed.current = false;
    setGreetingText(getGreeting(userData));
    setPendingUser(userData);
    setLoginPhase('greeting-in');

    // Minimum greeting display time (2.5s), then check if dashboard is ready
    setTimeout(() => {
      greetingMinElapsed.current = true;
      tryBeginFadeOut();
    }, 2500);
  };

  const handleDashboardReady = useCallback(() => {
    dashboardReady.current = true;
    tryBeginFadeOut();
  }, [tryBeginFadeOut]);

  const handleLogout = async () => {
    await fetch(`${API_URL}/api/users/logout`, {
      method: 'POST',
      credentials: 'include'
    });
    // Drop every persisted per-page slot. usePersistedState keys on the PAGE,
    // not the user, and sessionStorage outlives a logout — so without this the
    // next person to log in on this tab inherits the previous user's filters,
    // pagination, expanded rows and open-record ids. Beyond the confusion (a
    // stale filter that matches nothing inside a narrower account's scope looks
    // like an empty system), one user's record ids should not survive into
    // another user's session.
    useNavStore.getState().clearAllPages();
    setUser(null);
    window.history.pushState({}, '', '/');
    setCurrentRoute('/');
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  // Public routes (no auth required)
  if (currentRoute === '/signup') {
    return <Signup />;
  }

  if (currentRoute === '/create-password') {
    return <CreatePassword />;
  }

  // Login greeting transition — Dashboard mounts hidden behind overlay
  if (loginPhase && !user) {
    return (
      <>
        <div className={`login-fade-wrapper ${loginPhase === 'greeting-in' || loginPhase === 'greeting-out' ? 'faded' : ''}`}>
          <Login onLogin={() => {}} />
        </div>
        <div className={`greeting-overlay ${loginPhase === 'greeting-in' ? 'visible' : ''} ${loginPhase === 'greeting-out' ? 'fading-out' : ''}`}>
          <h1 className="greeting-text">{greetingText}</h1>
        </div>
        {/* Mount Dashboard behind the overlay so it can load */}
        {pendingUser && (
          <div style={{ position: 'fixed', inset: 0, zIndex: -1, opacity: 0, pointerEvents: 'none' }}>
            <Dashboard user={pendingUser} onLogout={handleLogout} onReady={handleDashboardReady} />
          </div>
        )}
      </>
    );
  }

  // Protected routes (auth required)
  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className={`dashboard-fade-wrapper ${loginPhase === 'dashboard-in' ? 'fading-in' : ''}`}>
      <Dashboard user={user} onLogout={handleLogout} />
    </div>
  );
}

export default App;